import { ComponentChildren } from 'preact';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { LabelValueLayout } from '../UNSAFE_LabelValueLayout';
import { StyledComponentProps } from '../utils/UNSAFE_typeUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { getFormControlClasses } from './FormControlUtils';
import { getReadonlyClasses, getTextFieldClasses, loadingStyles } from './TextFieldUtils';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { Size } from '../utils/UNSAFE_size';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type DivProps = HTMLAttributesSignalExcluded<HTMLDivElement>;
type UAProps = {
  inlineUserAssistance?: ComponentChildren;
};
type InnerOuterClassNamesProps = {
  outerClassNames?: string;
  innerClassNames?: string;
};

type StartTopLabelReadonlyTextFieldProps = Omit<DivProps, 'label'> &
  UAProps &
  InnerOuterClassNamesProps & {
    label?: ComponentChildren;
    labelEdge?: 'start' | 'top';
    labelStartWidth?: Size;
  };

const StartTopLabelReadonlyTextField = ({
  outerClassNames,
  innerClassNames,
  label,
  labelEdge,
  labelStartWidth,
  inlineUserAssistance,
  children,
  ...props
}: StartTopLabelReadonlyTextFieldProps) => {
  return (
    <div {...props} class={outerClassNames}>
      <LabelValueLayout label={label} labelEdge={labelEdge} labelStartWidth={labelStartWidth}>
        <div class={innerClassNames}>{children}</div>
      </LabelValueLayout>
      {inlineUserAssistance}
    </div>
  );
};

type InsideLabelReadonlyTextFieldProps = DivProps & UAProps & InnerOuterClassNamesProps;
const InsideLabelReadonlyTextField = ({
  outerClassNames,
  innerClassNames,
  inlineUserAssistance,
  children,
  ...props
}: InsideLabelReadonlyTextFieldProps) => {
  return (
    <div {...props} class={outerClassNames}>
      <div class={innerClassNames}>{children}</div>
      {inlineUserAssistance}
    </div>
  );
};

export type StyleProps = UAProps & {
  label?: ComponentChildren;
  labelEdge?: 'inside' | 'start' | 'top';
  variant?: 'textarea';
};

type Props = StyledComponentProps<'div', StyleProps>;

export const ReadonlyTextField = ({ label, labelEdge, children, variant, ...props }: Props) => {
  const { isFormLayout, isReadonly: isReadonlyForm } = useFormContext();
  const { isLoading } = useFormFieldContext();
  const hasInsideLabel = label !== undefined && labelEdge === 'inside';

  // TODO: We need to re-evaluate how we use CSS multi-classing to our VDOM components
  const formControlClassNames = getFormControlClasses(hasInsideLabel);
  const textFieldClassNames = getTextFieldClasses(true);
  const readonlyClassNames = getReadonlyClasses(
    isFormLayout && !isReadonlyForm,
    hasInsideLabel,
    variant
  );
  const outerClassNames = classNames([formControlClassNames, textFieldClassNames]);
  const innerClassNames = classNames([readonlyClassNames, isLoading && loadingStyles]);

  if (label !== undefined && (labelEdge === 'start' || labelEdge === 'top')) {
    return (
      <StartTopLabelReadonlyTextField
        outerClassNames={outerClassNames}
        innerClassNames={innerClassNames}
        label={label}
        labelEdge={labelEdge}
        {...props}>
        {children}
      </StartTopLabelReadonlyTextField>
    );
  }

  return (
    <InsideLabelReadonlyTextField
      outerClassNames={outerClassNames}
      innerClassNames={innerClassNames}
      {...props}>
      {label}
      {children}
    </InsideLabelReadonlyTextField>
  );
};
